/**
 * Teams Access Control Schemas
 *
 * NexusApi schemas for Teams user/group search and assignment.
 * Used for deployment access control (who can install the bot).
 */

import { z } from "zod";

// ============================================================
// SEARCH USERS
// ============================================================

export const SearchTeamsUsersQuerySchema = z.object({
  search: z.string().min(2, "Search query must be at least 2 characters")
});

export const TeamsUserSchema = z.object({
  id: z.string().uuid(),
  displayName: z.string(),
  userPrincipalName: z.string().email(),
  mail: z.string().email().optional(),
  jobTitle: z.string().optional(),
  department: z.string().optional(),
  officeLocation: z.string().optional()
});

export const SearchTeamsUsersResponseSchema = z.array(TeamsUserSchema);

export type TeamsUser = z.infer<typeof TeamsUserSchema>;

// ============================================================
// SEARCH GROUPS
// ============================================================

export const SearchTeamsGroupsQuerySchema = z.object({
  search: z.string().min(2, "Search query must be at least 2 characters")
});

export const TeamsGroupSchema = z.object({
  id: z.string().uuid(),
  displayName: z.string(),
  description: z.string().optional(),
  mailEnabled: z.boolean().optional(),
  securityEnabled: z.boolean().optional(),
  memberCount: z.number().optional()
});

export const SearchTeamsGroupsResponseSchema = z.array(TeamsGroupSchema);

export type TeamsGroup = z.infer<typeof TeamsGroupSchema>;

// ============================================================
// GET GROUP MEMBERS
// ============================================================

export const GetGroupMembersPathVarsSchema = z.object({
  groupId: z.string().uuid()
});

export const GetGroupMembersQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(100).optional().default(10)
});

export const GroupMemberSchema = z.object({
  id: z.string().uuid(),
  displayName: z.string(),
  mail: z.string().email().optional(),
  userPrincipalName: z.string().email().optional(),
  jobTitle: z.string().optional()
});

export const GetGroupMembersResponseSchema = z.object({
  count: z.number(),
  members: z.array(GroupMemberSchema)
});

export type GroupMember = z.infer<typeof GroupMemberSchema>;
export type GetGroupMembersResponse = z.infer<typeof GetGroupMembersResponseSchema>;

// ============================================================
// BULK IMPORT USERS
// ============================================================

export const BulkImportTeamsUsersBodySchema = z.object({
  emails: z.array(z.string().email()).min(1, "At least one email required")
});

export const BulkImportResultSchema = z.object({
  valid: z.array(TeamsUserSchema),
  invalid: z.array(
    z.object({
      email: z.string(),
      reason: z.string()
    })
  )
});

export type BulkImportResult = z.infer<typeof BulkImportResultSchema>;
